import React, { useCallback, useMemo } from "react";
import DebouncedInput from "@/react/components/DebouncedInput";
import StyledToggleButton from "@/react/components/styled/StyledToggleButton";
import Select, { VirtualMenuList } from "@8x8/oxygen-select";
import { InfoSmallIcon, PasteIcon } from "@8x8/oxygen-icon";
import { errorNotification } from "@/react/notifications";
import Tooltip from "@8x8/oxygen-tooltip";
import { notify, TYPE } from "@8x8/oxygen-toaster";
import { EllipsisOverflowSpan } from "@/react/components/table/components/filter/styles";
import FullWidthDiv from "@/react/features/styled/FullWidthDiv";
import styled from "styled-components";
import { Button } from "@/react/styled";

const StyledPasteIcon = styled(PasteIcon)`
  margin-right: 16px;
`;

const ListButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
`;

export const StringFilter = ({ filterValue, setFilterValue }) => {
  const columnFilterValue = filterValue ?? "";
  return (
    <DebouncedInput
      type="text"
      value={columnFilterValue ?? ""}
      onChange={(value) => setFilterValue(value)}
      placeholder={`Search...`}
    />
  );
};

export const StringFilterDisplay = (displayFilter) => {
  const operator = displayFilter.filterFnId === "is" ? "=" : ":";
  return (
    <EllipsisOverflowSpan>
      {operator} {displayFilter.value || ""}
    </EllipsisOverflowSpan>
  );
};

export const NumberFilter = ({ filterValue, setFilterValue }) => {
  const columnFilterValue = filterValue ?? "";
  return (
    <DebouncedInput
      type="number"
      value={columnFilterValue ?? ""}
      onChange={(value) => setFilterValue(value)}
      placeholder={`Number...`}
    />
  );
};

export const BooleanFilter = ({ filterValue, setFilterValue }) => {
  const isIndeterminate = filterValue === undefined;
  return (
    <StyledToggleButton
      isChecked={Boolean(filterValue)}
      isIndeterminate={isIndeterminate}
      label={filterValue === true ? "Yes" : "No"}
      onChange={() => setFilterValue(!Boolean(filterValue))}
    />
  );
};

export const BooleanFilterDisplay = (displayFilter) => (
  <EllipsisOverflowSpan>
    = {displayFilter.value === true ? "Yes" : "No"}
  </EllipsisOverflowSpan>
);

export const ListFilter = ({ filterValue, setFilterValue, column }) => {
  const options = useMemo(
    () =>
      Array.from(column.getFacetedUniqueValues().keys()).map((value) => ({
        value,
        label: value,
      })),
    [column]
  );
  const selectValues =
    filterValue === null
      ? []
      : options.filter((option) => filterValue.includes(option.value));
  const onChange = (selectedOptions) =>
    setFilterValue(selectedOptions.map((opt) => opt.value));

  const onAddFromClipboard = useCallback(() => {
    navigator.clipboard
      .readText()
      .then((text) => {
        const values = text
          .split("\n")
          .filter((v) => v)
          .map((v) => v.trim());
        let notFoundInOptions = [];
        const newValues = values.filter((value) => {
          if (!options.find((option) => option.value === value)) {
            notFoundInOptions.push(value);
            return false;
          }
          return true;
        });

        if (newValues.length > 0) {
          notify({
            type: TYPE.success,
            content: `Selected ${newValues.length} from clipboard`,
            duration: 5000,
          });

          setFilterValue(newValues);
        }

        if (notFoundInOptions.length) {
          errorNotification(
            new Error(
              `The following items were not found in the table: ${notFoundInOptions.join(
                ", "
              )}`
            )
          );
        }
      })
      .catch((err) => {
        errorNotification(
          new Error(`Failed to read clipboard contents: ${err.message}`)
        );
      });
  }, [options, setFilterValue]);

  return (
    <FullWidthDiv>
      <Select
        value={selectValues}
        options={options}
        onChange={onChange}
        isSearchable
        isMulti
        hasShortMultiDisplay={true}
        multipleSelectMaxRows={100}
        components={{
          MenuList: VirtualMenuList,
        }}
      />
      <ListButtonsContainer>
        <Button marginLeft={16} onClick={onAddFromClipboard} variant="tertiary">
          <StyledPasteIcon size={18} />
          <span>Paste from clipboard</span>
        </Button>
        <Tooltip title="Copy items from Excel or any line-separated text to be added to the filter">
          <InfoSmallIcon />
        </Tooltip>
      </ListButtonsContainer>
    </FullWidthDiv>
  );
};

export const NumberRangeFilter = ({ filterValue, setFilterValue }) => {
  const columnFilterValue = filterValue ?? "";
  return (
    <DebouncedInput
      type="text"
      value={columnFilterValue ?? ""}
      onChange={(value) => setFilterValue(value)}
      placeholder={`Search...`}
    />
  );
};

export const SelectFilter = ({ filterValue, setFilterValue, column }) => {
  const options = Object.keys(column.columnDef.options).map((key) => ({
    value: key,
    label: column.columnDef.options[key],
  }));
  const selectedValue = options.find((option) => filterValue === option.value);
  const onChange = (option) => setFilterValue(option.value);

  return (
    <FullWidthDiv>
      <Select
        value={selectedValue}
        options={options}
        onChange={onChange}
        isSearchable
        isClearable={false}
      />
    </FullWidthDiv>
  );
};

export const SelectFilterDisplay = (displayFilter) => {
  const options = displayFilter.column.columnDef.options;
  return (
    <EllipsisOverflowSpan>
      = {options[displayFilter.value]}
    </EllipsisOverflowSpan>
  );
};

export const isEmptyFilterFn = [
  "is empty",
  {
    label: "Is empty",
    filterFn: (row, columnId) => {
      const value = row.getValue(columnId);
      return value === undefined || value === null || value === "";
    },
    displayComponent: (displayFilter) => displayFilter.filterFnId,
  },
];

export const isntEmptyFilterFn = [
  "isn't empty",
  {
    label: "Isn't empty",
    filterFn: (row, columnId) => {
      const value = row.getValue(columnId);
      return value !== undefined && value !== null && value !== "";
    },
    displayComponent: (displayFilter) => displayFilter.filterFnId,
  },
];
